import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { apiEntities } from 'apis';
import { PageWrapper } from 'layout/PublicLayout/Wrappers';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import QuillRichTextShower from 'ui-component/htmlEditor/QuillRIchTextShower';

const PublicFaqView = () => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(null);

    const faqQuery = useQuery('public-faqs', apiEntities.getPublicFaqs);

    const faqs = useMemo(() => faqQuery.data?.data ?? [], [faqQuery.data?.data]);

    return (
        <PageWrapper>
            <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
                <Stack spacing={2.5} sx={{ maxWidth: 1280, mx: 'auto' }}>
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#111827' }}>
                        {t('FrequentlyAskedQuestions')}
                    </Typography>

                    {faqQuery.isLoading && (
                        <Stack spacing={2}>
                            {[1, 2, 3].map((item) => (
                                <Skeleton key={item} variant="rounded" height={100} sx={{ borderRadius: 3 }} />
                            ))}
                        </Stack>
                    )}

                    {!faqQuery.isLoading && faqs.length > 0 && (
                        <>
                            <Divider />
                            <Stack spacing={2}>
                                {faqs.map((faq) => {
                                    const faqId = faq.id ?? faq.Id;
                                    return (
                                        <Accordion
                                            key={faqId}
                                            expanded={expanded === faqId}
                                            onChange={() => setExpanded((current) => (current === faqId ? null : faqId))}
                                            disableGutters
                                            sx={{
                                                border: '1px solid #d9e6f2',
                                                borderRadius: '12px !important',
                                                background: '#fff',
                                                boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)',
                                                overflow: 'hidden',
                                                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                                                '&::before': { display: 'none' },
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 12px 32px rgba(36, 76, 118, 0.16)',
                                                    borderColor: '#7db7ef'
                                                },
                                                '&.Mui-expanded': {
                                                    margin: 0,
                                                    borderColor: '#7db7ef'
                                                }
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{ px: { xs: 1.5, md: 2.5 }, py: 0.75, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            width: 42,
                                                            height: 42,
                                                            flexShrink: 0,
                                                            borderRadius: 2,
                                                            color: '#075aaa',
                                                            background: '#e8f3ff'
                                                        }}
                                                    >
                                                        <HelpOutlineIcon />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0, flexGrow: 1, '& .ql-editor': { p: 0, fontWeight: 800, color: '#003b73' } }}>
                                                        <QuillRichTextShower html={faq.questionHtml} />
                                                    </Box>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, borderTop: '1px solid #e5edf5' }}>
                                                <Box sx={{ '& .ql-editor': { p: 0, color: '#334155' } }}>
                                                    <QuillRichTextShower html={faq.answerHtml} />
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Stack>
                        </>
                    )}

                    {!faqQuery.isLoading && faqs.length === 0 && (
                        <Box sx={{ p: 5, textAlign: 'center', borderRadius: 3, background: '#fff', border: '1px solid #e3e8ef' }}>
                            <Typography variant="h3">{t('NoDataFound')}</Typography>
                        </Box>
                    )}
                </Stack>
            </Box>
        </PageWrapper>
    );
};

export default PublicFaqView;
